import { useEffect } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Drawer from "@mui/material/Drawer";
import { useNavData } from "./config-navigation";
import { useResponsive } from "src/hooks/useResponsive";
import Logo from "src/components/Logo";
import { usePathname } from "src/hooks/usePathname";
import ScrollBar from "src/components/ScrollBar";
import NavSectionVertical from "src/components/header/nav/NavSectionVertical";
import { alpha } from "@mui/material/styles";

type Props = {
  openNav: boolean;
  onCloseNav: VoidFunction;
};

export default function NavVertical({ openNav, onCloseNav }: Props) {
  const pathname = usePathname();

  const lgUp = useResponsive("up", "lg");

  const navData = useNavData();

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <ScrollBar
      sx={{
        height: 1,
        "& .simplebar-content": {
          height: 1,
          display: "flex",
          flexDirection: "column",
        },
        "& .simplebar-track": {
          background: "transparent",
        },
        "& .simplebar-scrollbar": {
          background: alpha("#667eea", 0.3),
          borderRadius: "4px",
          "&:before": {
            background: "#667eea",
            borderRadius: "4px",
          },
        },
      }}
    >
      <Box
        sx={{
          p: 3,
          mb: 3,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "2px",
            background:
              "linear-gradient(90deg, #667eea 0%, #764ba2 50%, #667eea 100%)",
            borderRadius: "0 0 2px 0",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "60%",
            height: "1px",
            background:
              "linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.3) 50%, transparent 100%)",
          },
        }}
      >
        <Logo sx={{ mb: 0, height: "auto" }} />
      </Box>

      <NavSectionVertical data={navData} />

      <Box sx={{ flexGrow: 1 }} />
    </ScrollBar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: 280 },
      }}
    >
      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: "fixed",
            width: 280,
            background: "linear-gradient(180deg, #f8f9ff 0%, #f1f3f9 100%)",
            borderRight: "1px solid rgba(102, 126, 234, 0.2)",
            boxShadow: "4px 0 20px rgba(102, 126, 234, 0.1)",
            backdropFilter: "blur(10px)",
            zIndex: 1200,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
            },
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: 280,
              background: "linear-gradient(180deg, #f8f9ff 0%, #f1f3f9 100%)",
              borderRight: "1px solid rgba(102, 126, 234, 0.2)",
              boxShadow: "4px 0 20px rgba(102, 126, 234, 0.1)",
              backdropFilter: "blur(10px)",
              zIndex: 1200,
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
              },
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
